import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Permission levels
export enum PermissionLevel {
  VIEWER = 'viewer',
  COMMENTER = 'commenter',
  EDITOR = 'editor',
  OWNER = 'owner',
}

// Permission types
export enum PermissionType {
  USER = 'user',
  GROUP = 'group',
  DOMAIN = 'domain',
  ANYONE = 'anyone',
}

// Permission item interface
export interface Permission {
  id: string;
  itemId: string; // ID of the file or folder
  type: PermissionType;
  targetId: string; // User ID, group ID, domain, etc.
  targetEmail?: string;
  targetName?: string;
  level: PermissionLevel;
  allowExpiry: boolean;
  expiryDate?: string;
  createdAt: string;
  createdBy: string;
  requirePassword: boolean;
  passwordHash?: string;
  accessCount: number;
  lastAccessed?: string;
  isInherited?: boolean;
  inheritedFrom?: string;
}

// Password protection interface
export interface PasswordProtection {
  password: string;
  expiryDays?: number;
}

// Permission state interface
interface PermissionsState {
  permissions: Permission[];
  isLoading: boolean;
  error: string | null;
  passwordVerified: Record<string, boolean>;
}

// Initial state
const initialState: PermissionsState = {
  permissions: [],
  isLoading: false,
  error: null,
  passwordVerified: {},
};

// Fetch permissions for an item
export const fetchPermissions = createAsyncThunk(
  'permissions/fetchPermissions',
  async (itemId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/permissions/${itemId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch permissions');
    }
  }
);

// Add a new permission
export const addPermission = createAsyncThunk(
  'permissions/addPermission',
  async ({ itemId, permission }: { itemId: string; permission: Partial<Permission> }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/permissions/${itemId}`, permission);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add permission');
    }
  }
);

// Update a permission
export const updatePermission = createAsyncThunk(
  'permissions/updatePermission',
  async ({ permissionId, updates }: { permissionId: string; updates: Partial<Permission> }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/permissions/${permissionId}`, updates);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update permission');
    }
  }
);

// Remove a permission
export const removePermission = createAsyncThunk(
  'permissions/removePermission',
  async (permissionId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/permissions/${permissionId}`);
      return permissionId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove permission');
    }
  }
);

// Add password protection
export const addPasswordProtection = createAsyncThunk(
  'permissions/addPasswordProtection',
  async (
    { itemId, protection }: { itemId: string; protection: PasswordProtection },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/permissions/${itemId}/password`, protection);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add password protection');
    }
  }
);

// Verify password
export const verifyPassword = createAsyncThunk(
  'permissions/verifyPassword',
  async ({ itemId, password }: { itemId: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/permissions/${itemId}/verify`, { password });
      return { itemId, verified: response.data.verified };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to verify password');
    }
  }
);

// Create the slice
const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    clearPermissionsError: (state) => {
      state.error = null;
    },
    clearPasswordVerificationStatus: (state) => {
      state.passwordVerified = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch permissions
      .addCase(fetchPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPermissions.fulfilled, (state, action: PayloadAction<Permission[]>) => {
        state.isLoading = false;
        state.permissions = action.payload;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Add permission
      .addCase(addPermission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPermission.fulfilled, (state, action: PayloadAction<Permission>) => {
        state.isLoading = false;
        state.permissions.push(action.payload);
      })
      .addCase(addPermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update permission
      .addCase(updatePermission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePermission.fulfilled, (state, action: PayloadAction<Permission>) => {
        state.isLoading = false;
        const index = state.permissions.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.permissions[index] = action.payload;
        }
      })
      .addCase(updatePermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Remove permission
      .addCase(removePermission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removePermission.fulfilled, (state, action: PayloadAction<string>) => {
        state.isLoading = false;
        state.permissions = state.permissions.filter((p) => p.id !== action.payload);
      })
      .addCase(removePermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Add password protection
      .addCase(addPasswordProtection.fulfilled, (state, action: PayloadAction<Permission>) => {
        const index = state.permissions.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.permissions[index] = action.payload;
        } else {
          state.permissions.push(action.payload);
        }
      })
      
      // Verify password
      .addCase(verifyPassword.fulfilled, (state, action: PayloadAction<{ itemId: string; verified: boolean }>) => {
        const { itemId, verified } = action.payload;
        state.passwordVerified[itemId] = verified;
      });
  },
});

export const { clearPermissionsError, clearPasswordVerificationStatus } = permissionsSlice.actions;
export default permissionsSlice.reducer; 